import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowLeft, ArrowRight, Tag, Copy, Banknote, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { FadeUp } from "@/components/Reveal";

const Field = ({ label, name, value, onChange, type = "text", required = true, full = false, placeholder = "" }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <label className="label text-muted-foreground block mb-2">{label}</label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      placeholder={placeholder}
      className="w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-accent transition-colors"
      data-testid={`checkout-${name}`}
    />
  </div>
);

export default function Checkout() {
  const { items, subtotal, clearCart } = useCart();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    full_name: "", email: "", address: "", city: "", postal_code: "", country: "", phone: "", notes: "",
  });
  const [code, setCode] = useState("");
  const [discounts, setDiscounts] = useState([]);
  const [placing, setPlacing] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("zelle");
  const [agreed, setAgreed] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const copy = (text, label) => {
    navigator.clipboard?.writeText(text);
    toast.success(`${label} copied`);
  };

  // Stack up to two codes: percentage + free-shipping + gift card each get one slot; bundle & BOGO codes are exclusive
  const percentCode = discounts.find((d) => d.type === "percent");
  const shipCode = discounts.find((d) => d.type === "free_shipping");
  const bundleCode = discounts.find((d) => d.type === "bundle_half");
  const bogoCode = discounts.find((d) => d.type === "bogo");
  const fixedCode = discounts.find((d) => d.type === "fixed");
  const exclusiveCode = bundleCode || bogoCode;
  const cheapestUnit = items.length ? Math.min(...items.map((i) => i.price)) : 0;
  const bundleAmount = bundleCode ? cheapestUnit * 0.5 : 0;
  const bogoUnits = [...items.flatMap((i) => Array(i.quantity).fill(i.price))].sort((a, b) => b - a);
  const bogoAmount = bogoCode ? bogoUnits.filter((_, idx) => idx % 2 === 1).reduce((s, p) => s + p, 0) : 0;
  const percentPart = percentCode ? (subtotal * (percentCode.percent_off || 0)) / 100 : 0;
  const fixedPart = fixedCode ? Math.min(fixedCode.balance ?? fixedCode.amount ?? 0, Math.max(0, subtotal - percentPart)) : 0;
  const discountAmount = Math.min(subtotal, percentPart + bundleAmount + bogoAmount + fixedPart);
  const codeFreeShip = Boolean(shipCode);
  const freeShip = codeFreeShip || subtotal >= 200;
  const shippingFlat = freeShip ? 0 : 13;
  const total = subtotal - discountAmount + shippingFlat;

  const applyCode = async () => {
    const c = code.trim().toUpperCase();
    if (!c) return;
    if (discounts.some((d) => d.code === c)) {
      toast.error("That code is already applied");
      return;
    }
    try {
      const { data } = await api.get(`/discounts/validate/${c}`);
      const isShip = data.type === "free_shipping";
      const isExclusive = data.type === "bundle_half" || data.type === "bogo";
      const minUnits = data.type === "bundle_half" ? 3 : 2;
      if (isExclusive) {
        if (discounts.length > 0) {
          toast.error(`${data.code} can't be combined with other codes`);
          return;
        }
        const unitCount = items.reduce((n, i) => n + i.quantity, 0);
        if (unitCount < minUnits) {
          toast.error(`${data.code} needs at least ${minUnits} items in your cart`);
          return;
        }
      } else if (exclusiveCode) {
        toast.error(`Remove ${exclusiveCode.code} to use another code`);
        return;
      }
      const SLOT_LABEL = { percent: "percentage", free_shipping: "free shipping", fixed: "gift card" };
      if (!isExclusive && discounts.some((d) => d.type === data.type)) {
        toast.error(`A ${SLOT_LABEL[data.type] || data.type} code is already applied`);
        return;
      }
      setDiscounts((d) => [...d, data]);
      setCode("");
      toast.success(data.type === "bundle_half" ? "Cheapest item 50% off applied" : data.type === "bogo" ? "Buy one get one free applied" : isShip ? "Free shipping applied" : data.type === "fixed" ? `$${data.balance ?? data.amount} gift card applied` : `${data.percent_off}% off applied`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const removeCode = (c) => setDiscounts((d) => d.filter((x) => x.code !== c));

  const placeOrder = async (e) => {
    e.preventDefault();
    if (items.length === 0) return;
    if (!agreed) {
      toast.error("Please agree to the Terms & Conditions to continue.");
      return;
    }
    setPlacing(true);
    try {
      const { data } = await api.post("/orders", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        shipping: form,
        discount_codes: discounts.map((d) => d.code),
        payment_method: paymentMethod,
      });
      setConfirmed(data);
      clearCart();
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setPlacing(false);
    }
  };

  if (confirmed) {
    const orderNo = confirmed.order_number || confirmed.id.slice(-6).toUpperCase();
    const zelleEmail = confirmed.payment_recipient || "coastchems@gmail.com";
    return (
      <main className="pt-32 md:pt-40 pb-32 min-h-screen" data-testid="order-confirmation">
        <div className="mx-auto max-w-[760px] px-6 md:px-12">
          <div className="text-center">
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200, damping: 15 }} className="mx-auto h-20 w-20 flex items-center justify-center bg-accent text-accent-foreground">
              <Check size={40} strokeWidth={1.5} />
            </motion.div>
            <h1 className="mt-10 font-display text-5xl md:text-6xl font-light tracking-tight">Order placed.</h1>
            <p className="mt-6 text-sm text-muted-foreground max-w-md mx-auto">
              Thank you, {confirmed.shipping.full_name.split(" ")[0]}. Complete your payment below via Zelle to confirm order <span className="font-mono text-foreground">#{orderNo}</span>.
            </p>
          </div>

          {/* Payment instructions */}
          <div className="mt-12 border border-border" data-testid="payment-instructions">
            <div className="flex items-center gap-3 border-b border-border p-6 bg-secondary">
              <Banknote size={22} strokeWidth={1.5} className="text-accent" />
              <div>
                <p className="label">Pay by Zelle</p>
                <p className="text-xs text-muted-foreground mt-1">Send the exact total, then we email your confirmation and ship.</p>
              </div>
            </div>

            <div className="divide-y divide-border">
              {/* Amount */}
              <div className="flex items-center justify-between p-6">
                <span className="label text-muted-foreground">Amount to send</span>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xl" data-testid="pay-amount">${confirmed.total.toFixed(2)}</span>
                  <button onClick={() => copy(confirmed.total.toFixed(2), "Amount")} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="copy-amount" aria-label="Copy amount"><Copy size={16} strokeWidth={1.5} /></button>
                </div>
              </div>
              {/* Zelle email */}
              <div className="flex items-center justify-between p-6">
                <span className="label text-muted-foreground">Zelle email</span>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-sm sm:text-base break-all" data-testid="pay-email">{zelleEmail}</span>
                  <button onClick={() => copy(zelleEmail, "Zelle email")} className="text-muted-foreground hover:text-foreground transition-colors shrink-0" data-testid="copy-email" aria-label="Copy Zelle email"><Copy size={16} strokeWidth={1.5} /></button>
                </div>
              </div>
              {/* Memo */}
              <div className="flex items-center justify-between p-6">
                <div>
                  <span className="label text-muted-foreground">Memo / Note</span>
                  <p className="text-xs text-destructive mt-1">Enter ONLY this order number — nothing else.</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xl tracking-widest" data-testid="pay-memo">{orderNo}</span>
                  <button onClick={() => copy(orderNo, "Order number")} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="copy-memo" aria-label="Copy order number"><Copy size={16} strokeWidth={1.5} /></button>
                </div>
              </div>
            </div>

            <div className="p-6 bg-secondary border-t border-border">
              <ol className="text-sm text-foreground/80 space-y-2 list-decimal list-inside">
                <li>Open your banking app and choose <span className="font-mono">Zelle</span>.</li>
                <li>Send <span className="font-mono">${confirmed.total.toFixed(2)}</span> to <span className="font-mono">{zelleEmail}</span>.</li>
                <li>Put <span className="font-mono">{orderNo}</span> in the memo — <span className="text-destructive">order number only</span>.</li>
                <li>Once received, we email confirmation to {confirmed.shipping.email} and begin shipping.</li>
              </ol>
            </div>
          </div>

          <div className="mt-10 text-center">
            <Link to="/shop" className="label inline-flex items-center gap-2 border-b border-foreground pb-1 hover:text-accent hover:border-accent transition-colors" data-testid="continue-shopping">
              Continue shopping <ArrowRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </main>
    );
  }

  if (items.length === 0) {
    return (
      <main className="pt-40 pb-32 min-h-screen">
        <div className="mx-auto max-w-[800px] px-6 md:px-12">
          <p className="label text-muted-foreground">Your cart is empty.</p>
          <Link to="/shop" className="mt-6 label inline-flex items-center gap-2 border-b border-foreground pb-1 hover:text-accent transition-colors">Browse shop</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-32 md:pt-40 pb-24 min-h-screen" data-testid="checkout-page">
      <div className="mx-auto max-w-[1200px] px-6 md:px-12">
        <Link to="/cart" className="inline-flex items-center gap-2 label text-muted-foreground hover:text-foreground transition-colors mb-8" data-testid="back-to-cart">
          <ArrowLeft size={16} strokeWidth={1.5} /> Back to cart
        </Link>
        <FadeUp>
          <p className="label text-accent mb-6">/ Checkout</p>
          <h1 className="font-display text-5xl md:text-6xl font-light leading-[0.9] tracking-tight">Shipping details</h1>
        </FadeUp>

        <form onSubmit={placeOrder} className="mt-14 grid md:grid-cols-12 gap-12" data-testid="checkout-form">
          <div className="md:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Field label="Full name" name="full_name" value={form.full_name} onChange={onChange} full />
              <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} full />
              <Field label="Address" name="address" value={form.address} onChange={onChange} full />
              <Field label="City" name="city" value={form.city} onChange={onChange} />
              <Field label="Postal code" name="postal_code" value={form.postal_code} onChange={onChange} />
              <Field label="Country" name="country" value={form.country} onChange={onChange} />
              <Field label="Phone (optional)" name="phone" value={form.phone} onChange={onChange} required={false} />
              <div className="sm:col-span-2">
                <label className="label text-muted-foreground block mb-2">Order notes (optional)</label>
                <textarea name="notes" value={form.notes} onChange={onChange} rows={3} className="w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-accent transition-colors resize-none" data-testid="checkout-notes" />
              </div>
            </div>

            <div className="mt-10" data-testid="payment-method-section">
              <p className="label text-muted-foreground mb-4">Payment method</p>
              <button
                type="button"
                onClick={() => setPaymentMethod("zelle")}
                className={`w-full flex items-center gap-4 border p-5 text-left transition-colors ${paymentMethod === "zelle" ? "border-primary bg-secondary" : "border-border hover:border-foreground"}`}
                data-testid="payment-zelle"
              >
                <span className={`h-4 w-4 rounded-full border flex items-center justify-center ${paymentMethod === "zelle" ? "border-primary" : "border-border"}`}>
                  {paymentMethod === "zelle" && <span className="h-2 w-2 rounded-full bg-primary" />}
                </span>
                <Banknote size={20} strokeWidth={1.5} className="text-accent" />
                <span>
                  <span className="block text-sm font-medium">Zelle</span>
                  <span className="block text-xs text-muted-foreground mt-0.5">Pay by bank transfer. Instructions appear after you place the order.</span>
                </span>
              </button>
            </div>
          </div>

          <div className="md:col-span-5">
            <div className="border border-border p-8 sticky top-28" data-testid="checkout-summary">
              <p className="label text-muted-foreground mb-6">Order</p>
              <div className="space-y-3 mb-6 pb-6 border-b border-border">
                {items.map((i) => (
                  <div key={i.product_id} className="flex justify-between text-sm">
                    <span className="text-foreground/80">{i.name} <span className="text-muted-foreground">× {i.quantity}</span></span>
                    <span className="font-mono">${(i.price * i.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </div>

              <div className={`flex gap-2 ${discounts.length ? "mb-3" : "mb-6"}`}>
                <div className="flex-1 flex items-center border border-border px-3">
                  <Tag size={14} strokeWidth={1.5} className="text-muted-foreground" />
                  <input
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    placeholder={discounts.length >= 2 ? "Max 2 codes applied" : discounts.length ? "Add a second code" : "Discount code"}
                    disabled={discounts.length >= 2}
                    className="flex-1 bg-transparent py-3 px-2 text-sm focus:outline-none placeholder:text-muted-foreground disabled:opacity-50"
                    data-testid="discount-input"
                  />
                </div>
                <button type="button" onClick={applyCode} disabled={discounts.length >= 2} className="label px-4 border border-border hover:bg-secondary transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-testid="apply-discount-btn">Apply</button>
              </div>
              {discounts.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6" data-testid="applied-codes">
                  {discounts.map((d) => (
                    <span key={d.code} className="label inline-flex items-center gap-2 border border-accent/40 text-accent px-2.5 py-1" data-testid={`applied-code-${d.code}`}>
                      {d.code} · {d.type === "free_shipping" ? "FREE SHIP" : d.type === "bundle_half" ? "CHEAPEST −50%" : d.type === "bogo" ? "BOGO FREE" : d.type === "fixed" ? `$${(d.balance ?? d.amount)} LEFT` : `${d.percent_off}% OFF`}
                      <button type="button" onClick={() => removeCode(d.code)} aria-label={`Remove ${d.code}`} className="hover:text-destructive transition-colors" data-testid={`remove-code-${d.code}`}><X size={12} strokeWidth={1.5} /></button>
                    </span>
                  ))}
                </div>
              )}

              <div className="flex justify-between text-sm mb-2">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="font-mono" data-testid="summary-subtotal">${subtotal.toFixed(2)}</span>
              </div>
              {percentCode && (
                <div className="flex justify-between text-sm mb-2 text-accent" data-testid="summary-discount">
                  <span>Discount ({percentCode.code})</span>
                  <span className="font-mono">− ${discountAmount.toFixed(2)}</span>
                </div>
              )}
              {shipCode && (
                <div className="flex justify-between text-sm mb-2 text-accent" data-testid="summary-freeship-code">
                  <span>Discount ({shipCode.code})</span>
                  <span className="font-mono">FREE SHIP</span>
                </div>
              )}
              {bundleCode && (
                <div className="flex justify-between text-sm mb-2 text-accent" data-testid="summary-bundle-discount">
                  <span>Discount ({bundleCode.code} — cheapest item 50% off)</span>
                  <span className="font-mono">− ${bundleAmount.toFixed(2)}</span>
                </div>
              )}
              {bogoCode && (
                <div className="flex justify-between text-sm mb-2 text-accent" data-testid="summary-bogo-discount">
                  <span>Discount ({bogoCode.code} — buy one get one free)</span>
                  <span className="font-mono">− ${bogoAmount.toFixed(2)}</span>
                </div>
              )}
              {fixedCode && (
                <div className="flex justify-between text-sm mb-2 text-accent" data-testid="summary-giftcard-discount">
                  <span>Gift card ({fixedCode.code})</span>
                  <span className="font-mono">− ${fixedPart.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm mb-2">
                <span className="text-muted-foreground">Shipping{freeShip ? "" : " (flat)"}</span>
                <span className="font-mono" data-testid="summary-shipping">{freeShip ? "FREE" : `$${shippingFlat.toFixed(2)}`}</span>
              </div>
              {!freeShip && subtotal > 0 && (
                <p className="text-xs text-accent mt-1 mb-2" data-testid="free-ship-hint">
                  Add ${(200 - subtotal).toFixed(2)} more for free shipping.
                </p>
              )}
              <div className="flex justify-between mt-4 pt-4 border-t border-border mb-8">
                <span className="label">Total</span>
                <span className="font-mono text-lg" data-testid="summary-total">${total.toFixed(2)}</span>
              </div>

              <div className="mt-6 mb-4 border-t border-border pt-6" data-testid="research-disclaimer">
                <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="terms-checkbox-label">
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    className="mt-1 h-5 w-5 accent-[hsl(var(--primary))] shrink-0"
                    data-testid="terms-checkbox"
                  />
                  <span className="text-sm text-foreground/85 leading-relaxed">
                    I acknowledge that all products purchased from <b>Coastal Chemistry</b> are intended solely for legitimate research, laboratory, or analytical purposes. I confirm that I will not use these products for human or animal consumption, injection, diagnostic, or therapeutic purposes. I understand that by placing this order, I agree to the{" "}
                    <Link to="/terms" target="_blank" rel="noreferrer" className="text-accent border-b border-accent" data-testid="terms-link">Research Use Policies &amp; Disclaimers</Link>. <span className="text-destructive">*</span>
                  </span>
                </label>
                <p className="mt-3 pl-8 text-xs italic text-muted-foreground">
                  Orders cannot be completed without confirming research-use compliance.
                </p>
              </div>

              <motion.button whileTap={{ scale: 0.98 }} type="submit" disabled={placing || !agreed} className="w-full h-14 bg-primary text-primary-foreground label hover:bg-accent hover:text-accent-foreground transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-testid="place-order-btn">
                {placing ? "Placing order…" : "Place order"} <ArrowRight size={16} strokeWidth={1.5} />
              </motion.button>
              <p className="mt-4 text-xs text-muted-foreground text-center">Pay by Zelle after placing — we confirm by email once received.</p>
            </div>
          </div>
        </form>
      </div>
    </main>
  );
}
